import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { Grid, Card, CardContent, Typography, Button, CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Logger from "./Logger";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const PrintDetails = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numCopies, setNumCopies] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [qrLinks, setQrLinks] = useState();
  const orderNo = sessionStorage.getItem('orderNo');
  const adminEmail = sessionStorage.getItem("adminEmail");
  const navigate = useNavigate();
  const summaryOrdersLength = sessionStorage.getItem('summaryOrdersLength');
  const type = sessionStorage.getItem("type");

  // Initialize Logger
  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/api/packer/details/${adminEmail}/${orderNo}/${type}`);

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminEmail, orderNo, type]);

  const generateQrLinks = async () => {
    const orderIds = data.orders
      ? `[${data.orders.map(order => order._id).join(',')}]`
      : `[${data._id}]`;

    if (!adminEmail || !orderIds) {
      return;
    }
    try {
      const response = await fetch(`/api/DrOrder/addQrData`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          adminEmail,
          orderIds
        }),
      });

      const data = await response.json();

      if (data && data.id) {
        setQrLinks(data.id);
      }
    } catch (error) {
      logger.log(`Error generating QR link: ${error.message}`, 'ERROR');
    }
  };

  const generatePDF = useCallback(async () => {
    if (!data) {
      return;
    }

    if (!qrLinks) {
      logger.log('Missing key value for PDF generation', 'ERROR');
      return;
    }

    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });

    const contentWidth = 216;
    const topMargin = 20;

    try {
      const qrCodeDataURL = await QRCode.toDataURL(qrLinks);

      for (let i = 0; i < numCopies; i++) {
        if (i > 0) doc.addPage();

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);

        if (data.type === "Summary") {
          doc.text("Summary Details", contentWidth / 2, topMargin + 10, { align: "center" });
        } else {
          doc.text("Order Details", contentWidth / 2, topMargin + 10, { align: "center" });
        }

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");

        const textStartX = contentWidth - 130;
        const qrCodeX = 10;
        const qrCodeSize = 75;
        const qrCodeY = topMargin + 20;
        let textStartY = topMargin + 35;
        let lineSpacing = 10;

        doc.addImage(qrCodeDataURL, "PNG", qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);

        if (data.type === "Summary") {
          doc.text(`S.No: ${data.summaryNo}`, textStartX, textStartY);
          textStartY += lineSpacing;
          doc.text(`No.of Orders: ${summaryOrdersLength}`, textStartX, textStartY);
        } else {
          doc.text(`Order: ${data.orderNo}`, textStartX, textStartY);
        }

        textStartY += lineSpacing;
        doc.text(`Packets: ${data.packageDetails.noOfPackets}`, textStartX, textStartY);

        textStartY += lineSpacing;
        doc.text(`Boxes: ${data.packageDetails.noOfBoxes}`, textStartX, textStartY);

        textStartY += lineSpacing;
        doc.text(`Weight: ${data.packageDetails.weightOfOrder}`, textStartX, textStartY);

        textStartY += lineSpacing;
        doc.text(`Area: ${data.area}`, textStartX, textStartY);
      }

      doc.autoPrint();
      window.open(doc.output("bloburl"), "_blank");

    } catch (error) {
      logger.log(`Error generating QR code or PDF: ${error.message}`, 'ERROR');
    } finally {
      setNumCopies(1);
    }
  }, [data, qrLinks, numCopies, summaryOrdersLength, logger]);

  useEffect(() => {
    if (qrLinks !== null) {
      generatePDF();
    }
  }, [qrLinks, generatePDF]);

  const handlePrint = () => {
    setOpenDialog(true);
  };

  const handleHomePageRedirect = () => {
    navigate('/packerLanding');
  };

  const confirmPrint = async () => {
    await generateQrLinks();
    setOpenDialog(false);
  };

  if (loading) return <CircularProgress sx={{ display: 'block', margin: '50px auto' }} />;
  if (error) return <Typography color="error" sx={{ textAlign: 'center', marginTop: '20px' }}>{error}</Typography>;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '80px' }}>
        <Typography variant="h5" sx={{ marginBottom: '15px', fontSize: "20px", marginTop: '-15px' }}>
          Details
        </Typography>

        <Card sx={{ padding: '10px', width: '40%', textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <Typography>Type: {data.type}</Typography>
            {/* Show Summary No only if type is "Summary" */}
            {data.type === "Summary" && (
              <Typography>Summary No: {orderNo}</Typography>
            )}
            <Typography>Order No: {data.orders ? data.orders.map(order => order.Order_No).join(', ') : data.orderNo}</Typography>
            <Typography>Area:  {data.area}</Typography>
          </CardContent>
        </Card>

        <Divider sx={{ width: '100%', marginY: '30px' }} />

        <Typography variant="h5" sx={{ marginBottom: '15px', fontSize: "20px", marginTop: '-15px' }}>
          Package Details
        </Typography>

        <Card sx={{ padding: '10px', width: '40%', textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <Typography>Number of Packets:  {data.packageDetails.noOfPackets}</Typography>
            <Typography>Number of Boxes: {data.packageDetails.noOfBoxes}</Typography>
            <Typography>Total Weight: {data.packageDetails.weightOfOrder} kg</Typography>
          </CardContent>
        </Card>

        <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '40%' }}>
          <Button variant="contained" color="primary" onClick={handleHomePageRedirect} sx={{ marginTop: '30px', width: '200px' }}>
            ← Go Back
          </Button>
          <Button variant="contained" color="primary" onClick={handlePrint} sx={{ marginTop: '30px', width: '200px' }}>
            Print →
          </Button>
        </Grid>
      </div>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Print Label</DialogTitle>
        <DialogContent>
          <Typography>How many copies do you want to print?</Typography>
          <TextField
            type="number"
            value={numCopies}
            onChange={(e) => setNumCopies(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
            sx={{ marginTop: '10px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={confirmPrint}
            color="primary"
            variant="contained"
            disabled={numCopies < 1} // Disable if value is less than 1
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PrintDetails;
